import type { DocumentNode } from '@apollo/client'

/**
 * Extracts the top-level root field name from a GraphQL query document.
 *
 * Example: `query GetStartPage { startPage { ... } }` returns `startPage`.
 * The root field acts as a stable cache key so each query document is the
 * single source of truth — no string keys need to be duplicated in app code.
 */
export function getQueryRootFieldName(query: DocumentNode): string {
    const operationDefinition = query.definitions.find(
        (definition) => definition.kind === 'OperationDefinition',
    )

    if (!operationDefinition || operationDefinition.kind !== 'OperationDefinition') {
        throw new Error('GraphQL document is missing an OperationDefinition')
    }

    const firstSelection = operationDefinition.selectionSet.selections[0]

    if (!firstSelection || firstSelection.kind !== 'Field') {
        throw new Error(
            'GraphQL operation must start with a field selection to derive a cache key',
        )
    }

    return firstSelection.name.value
}

/**
 * Returns the operation name (e.g. `GetStartPage`) if present on the document.
 */
export function getQueryOperationName(query: DocumentNode): string | undefined {
    const operationDefinition = query.definitions.find(
        (definition) => definition.kind === 'OperationDefinition',
    )

    if (!operationDefinition || operationDefinition.kind !== 'OperationDefinition') {
        return undefined
    }

    return operationDefinition.name?.value
}
